import React, { useEffect, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

// Set up the worker
// Using a CDN hosted worker because the local one might not be easily accessible in all environments
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface PdfImageProps {
  base64: string;
  className?: string;
  alt?: string;
  fileType?: string;
  onConverted?: (imageUrls: string[]) => void;
}

const PdfImage: React.FC<PdfImageProps> = ({
  base64,
  className,
  alt = "Preview",
  fileType,
  onConverted,
}) => {
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [textContent, setTextContent] = useState<string>("");

  useEffect(() => {
    let isMounted = true;

    const convert = async () => {
      try {
        setLoading(true);
        setError(false);

        // Clean base64 string
        const base64Content = base64.includes(",")
          ? base64.split(",")[1]
          : base64;

        // Check if this is a text file
        const isTextFile =
          fileType && /\.(txt|csv|log|json|xml)$/i.test(fileType);

        if (isTextFile) {
          // For text files, decode and display as text
          try {
            const decodedText = atob(base64Content);
            if (isMounted) {
              setTextContent(decodedText);
              setLoading(false);
            }
          } catch (e) {
            if (isMounted) {
              setError(true);
              setLoading(false);
            }
          }
          return;
        }

        // Check if this is an image file
        const isImageFile =
          fileType && /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(fileType);

        if (isImageFile) {
          // For image files, create a data URL directly
          const imageUrl = `data:image/${fileType?.replace(".", "")};base64,${base64Content}`;
          if (isMounted) {
            setImageUrls([imageUrl]);
            if (onConverted) {
              onConverted([imageUrl]);
            }
            setLoading(false);
          }
          return;
        }

        // For PDF files, use the PDF.js library
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;

        const pages: string[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          // Use a higher scale for better quality
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) throw new Error("Canvas context not available");

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas,
          }).promise;

          pages.push(canvas.toDataURL("image/png"));
        }

        if (isMounted) {
          setImageUrls(pages);
          if (onConverted) {
            onConverted(pages);
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    if (base64) {
      convert();
    }

    return () => {
      isMounted = false;
    };
  }, [base64, fileType]);

  if (loading) {
    return (
      <div
        className={` bg-muted rounded ${className}`}
        style={{ height: "200px" }}
      />
    );
  }

  // Handle text content
  if (textContent) {
    return (
      <div
        className={`border rounded p-4 bg-muted overflow-auto ${className}`}
        style={{ maxHeight: "400px" }}
      >
        <pre
          style={{
            margin: 0,
            fontSize: "12px",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        >
          {textContent}
        </pre>
      </div>
    );
  }

  if (error || imageUrls.length === 0) {
    return (
      <div
        className={`flex items-center justify-center border rounded bg-muted text-muted-foreground p-4 ${className}`}
      >
        <p className="text-xs">Failed to load preview</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {imageUrls.map((url, index) => (
        <img
          key={index}
          src={url}
          alt={`${alt} - Page ${index + 1}`}
          className={className}
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      ))}
    </div>
  );
};

export default PdfImage;
